/* eslint-disable import/order */
/* eslint-disable class-methods-use-this */
import Request from '@hotmart-org-ca/request'

import RequestHelper from 'common/helpers/request'
import { User, GetProfileResponse, LocalStorageKey } from '../types'
import { AuthProvider, CasUser } from '../providers/types'
import CasProvider from '../providers'
import request from 'core/config/request'

import './request'

class AuthService {
  private userData: User = {} as User

  constructor(private readonly authProvider: AuthProvider) {
    this.authProvider = authProvider
  }

  start() {
    this.authProvider.start()
  }

  stop() {
    this.authProvider.stop()
    this.updateHeaders('')
  }

  subscribe({ next, error }: { next: (user: User) => void | Promise<void>, error: (error: Error) => void }) {
    /**
     * This application does not have a logout button.
     * If a user is logged in and logs out from another page,
     * they will be logged out when returning to the application.
     * To ensure proper functionality, it is necessary to check if the user is anonymous.
     * If they are, certain keys in localStorage should be cleared.
     */
    this.authProvider.subscribe({
      next: async userCas => {
        if (this.hasAnonymousToken()) {
          this.clear()
        }
        this.updateHeaders(userCas.id_token)
        await this.parseUserData(userCas)
        localStorage.setItem(LocalStorageKey.USER, JSON.stringify({ user: this.userData }))

        next(this.userData)
      },
      error: err => {
        this.stop()

        if (!this.hasAnonymousToken()) {
          this.clear()
        }

        error(err)
      }
    })
  }

  async logout() {
    return this.authProvider.logout().then(() => {
      this.clear()
    })
  }

  private async parseUserData(userData: CasUser) {
    let profile = {} as GetProfileResponse

    try {
      profile = await this.getProfile() ?? {} as GetProfileResponse
    } catch (e) {
      console.error('erro to get profile', e)
    }

    this.userData = {
      token: userData?.id_token,
      authorities: userData?.profile?.authorities ?? [],
      email: userData?.profile?.email ?? '',
      name: userData.profile?.name ?? '',
      tradeName: userData?.profile?.tradeName ?? '',
      id: Number(userData?.profile?.id),
      locale: userData?.profile?.attributes?.locale ?? '',
      photo: profile?.urlPhoto ?? ''
    }
  }

  getItem(item: string) {
    return localStorage.getItem(item)
  }

  hasAnonymousToken() {
    return !!localStorage.getItem(LocalStorageKey.ANONYMOUS_TOKEN_WEBINAR)
  }

  isAnonymous() {
    const hasUser = Object.keys(this?.userData ?? {}).length > 0

    if (hasUser) {
      return false
    }

    return this.hasAnonymousToken()
  }

  setAnonymousToken(token: string) {
    localStorage.setItem(LocalStorageKey.ANONYMOUS_TOKEN_WEBINAR, token)
  }

  getAnonymousToken() {
    return localStorage.getItem(LocalStorageKey.ANONYMOUS_TOKEN_WEBINAR)
  }

  removeAnonymousToken() {
    this.clear()
    this.updateHeaders('')
  }

  getToken() {
    const user = JSON.parse(localStorage.getItem(LocalStorageKey.USER) || '{}') as {user: User}

    return user?.user?.token ?? ''
  }

  async getUserByToken() {
    try {
      return await Request('auth.token.check')
    } catch (e) {
      throw RequestHelper.errorHandling(e)
    }
  }

  async getProfile() {
    try {
      const response = await Request('auth.user_profile')

      return response as GetProfileResponse
    } catch (e) {
      console.error(e)
      return null
    }
  }

  clear() {
    localStorage.removeItem(LocalStorageKey.USER)
    localStorage.removeItem(LocalStorageKey.ANONYMOUS_TOKEN_WEBINAR)
    localStorage.removeItem(LocalStorageKey.LOGIN_REDIRECT)
    localStorage.removeItem(LocalStorageKey.TOKEN)
    localStorage.removeItem(LocalStorageKey.LEAD)
  }

  updateHeaders(token: string) {
    localStorage.setItem(LocalStorageKey.TOKEN, token)
    Request.setDefaultHeaders({ token })

    if (token) {
      request.defaults.headers.common.Authorization = `Bearer ${token}`
    } else {
      delete request.defaults.headers.common.Authorization
    }
  }

  async logoutUser() {
    await this.authProvider.logout()
  }

  getUser() {
    return this.userData
  }
}

export default new AuthService(new CasProvider())
